import { PageTitle } from "@/components/custom/PageTitle"
import { useNavigate, Link } from "react-router-dom"
import { DataTable } from "./DataTable"
import { columns } from "./columns"
import { useDispatch, useSelector } from "react-redux"
import { AppDispatch, RootState } from "@/redux"
import { getPageButtonText } from "@/helpers/button"
import { useEffect, useMemo, useState } from "react"
import { workflowActions } from "@/redux/slice/workflowSlice"
import { WorkflowStatus } from "@/constants/workflows/workflows"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { ButtonVariant } from "@/constants/button/button"
import { Delete } from "lucide-react"
import { Pagination } from "@/components/custom/Pagination/Pagination"
import { INPUT_FIELD_CLASSNAME } from "@/constants/classname/classname"
import { getSubscription } from "@/services/billingService"
import { getQuotaDisplay } from "@/helpers/billingQuota"
import { isActiveSubscription } from "@/helpers/billing"

const DEFAULT_PAGE_SIZE = 10

/** Workflows that count toward playbook quota (ACTIVE + INACTIVE + PROCESSING per product guide) */
const QUOTA_COUNT_STATUSES: string[] = [WorkflowStatus.ACTIVE, WorkflowStatus.IN_ACTIVE, WorkflowStatus.PROCESSING]

export const Workflows = () => {
  const navigate = useNavigate()
  const dispatch: AppDispatch = useDispatch()
  const { userData, token } = useSelector((state: RootState) => state.user)
  const currentOrgId = userData?.currentOrganization?.id
  const { workflows } = useSelector((state: RootState) => state.workflow)
  const billingSubscription = useSelector((state: RootState) => state.billing?.subscription)
  const [searchQuery, setSearchQuery] = useState("")
  const [paginationState, setPaginationState] = useState({ limit: DEFAULT_PAGE_SIZE, offset: 0 })

  useEffect(() => {
    if (token && currentOrgId) {
      dispatch(getSubscription(token, currentOrgId))
    }
  }, [token, currentOrgId, dispatch])

  useEffect(() => {
    dispatch(workflowActions.resetWorkflowCreate())
  }, [dispatch])

  const playbookQuotaUsed = useMemo(() =>
    workflows.filter(w => QUOTA_COUNT_STATUSES.includes(w.status)).length
  , [workflows])

  const playbookQuotaTotal = useMemo(() => {
    const sub = billingSubscription
    if (!sub) return 0
    const quota = getQuotaDisplay(sub)
    return quota.totalQuota
  }, [billingSubscription])

  // billingSubscription === undefined  → still loading (button disabled until confirmed)
  // billingSubscription === null       → loaded, no subscription found
  // billingSubscription === object with active status   → has an active subscription
  // billingSubscription === object with inactive status → treated as no subscription
  const isSubscriptionLoading = billingSubscription === undefined
  const hasActiveSubscription = isActiveSubscription(billingSubscription)
  const hasNoSubscription = !isSubscriptionLoading && !hasActiveSubscription

  const isQuotaBased =
    billingSubscription?.pricingModel === 'playbook_based_v2' ||
    billingSubscription?.planTier === 'free_quota_only' ||
    billingSubscription?.status === 'free_quota_only'
  const atQuota = Boolean(
    isQuotaBased &&
    playbookQuotaTotal > 0 &&
    playbookQuotaUsed >= playbookQuotaTotal
  )

  const handleAddWorkflowClick = () => {
    navigate('/workflows/select-source')
  }

  const sortedWorkflows = useMemo(() => (
    workflows
      .filter(workflow => workflow.status !== WorkflowStatus.DELETED)
      .sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime())
  ), [workflows])

  const filteredWorkflows = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase()
    if (!normalizedQuery) return sortedWorkflows
    return sortedWorkflows.filter(workflow => (workflow.nickname ?? "").toLowerCase().includes(normalizedQuery))
  }, [sortedWorkflows, searchQuery])

  useEffect(() => {
    setPaginationState(prev => {
      const total = filteredWorkflows.length
      if (total === 0) {
        return prev.offset === 0 ? prev : { ...prev, offset: 0 }
      }
      const lastPageOffset = Math.floor((total - 1) / prev.limit) * prev.limit
      if (prev.offset > lastPageOffset) {
        return { ...prev, offset: lastPageOffset }
      }
      return prev
    })
  }, [filteredWorkflows.length, paginationState.limit])

  const workflowsToShow = useMemo(() => {
    const start = paginationState.offset
    const end = start + paginationState.limit
    return filteredWorkflows.slice(start, end)
  }, [filteredWorkflows, paginationState])

  return (
    <div className="protectedPageLayout">
      <PageTitle
        title="Workflows"
        buttonText={getPageButtonText(userData?.role, 'Add New Workflow')}
        onButtonClick={handleAddWorkflowClick}
        buttonDisabled={isSubscriptionLoading || hasNoSubscription || atQuota}
      />
      {hasNoSubscription && (
        <div className="mb-4 w-full rounded-lg border border-blue-200 bg-blue-50 p-4" role="status">
          <p className="text-sm font-medium text-blue-900">
            No active subscription yet.
          </p>
          <p className="mt-1 text-sm text-blue-700">
            Subscribe to start building workflows.{' '}
            <Link to="/billing" className="font-medium underline hover:no-underline">View Plans →</Link>
          </p>
        </div>
      )}
      {atQuota && (
        <div className="mb-4 w-full rounded-lg border border-amber-200 bg-amber-50 p-4" role="alert">
          <p className="text-sm font-medium text-amber-800">
            Playbook quota reached ({playbookQuotaUsed} of {playbookQuotaTotal} in use).
          </p>
          <p className="mt-1 text-sm text-amber-700">
            Add more playbooks in <Link to="/billing" className="font-medium underline hover:no-underline">Billing</Link> to create new workflows.
          </p>
        </div>
      )}
      <div className="w-full flex flex-col gap-4 mb-4 md:flex-row md:items-center md:justify-between">
        <div className="flex w-full items-center gap-2 md:max-w-md relative">
          <Input
            value={searchQuery}
            onChange={(event) => {
              setSearchQuery(event.target.value)
              setPaginationState(prev => ({ ...prev, offset: 0 }))
            }}
            placeholder="Search workflows"
            className={`${INPUT_FIELD_CLASSNAME} bg-foreground text-primary`}
            aria-label="Search workflows"
          />
          {searchQuery && (
            <Button
              type="button"
              variant={ButtonVariant.ICON}
              className="h-9 text-destructive rounded rounded-md bg-destructive-foreground absolute right-[2px]"
              onClick={() => setSearchQuery("")}
              disabled={!searchQuery.trim()}
            >
              <Delete size={16} />
            </Button>
          )}
        </div>
      </div>
      <DataTable columns={columns} data={workflowsToShow} />
      <Pagination
        total={filteredWorkflows.length}
        limit={paginationState.limit}
        offset={paginationState.offset}
        onPageChange={(newOffset) => setPaginationState(prev => ({ ...prev, offset: newOffset }))}
        onLimitChange={(newLimit) => setPaginationState({ limit: newLimit, offset: 0 })}
      />
    </div>
  )
}
